import { onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useBooksyAssistantStore } from '../store'

export const ASSISTANT_PANE_DEFAULT_WIDTH = 400
export const ASSISTANT_PANE_MIN_WIDTH = 320
export const ASSISTANT_PANE_MAX_WIDTH = 640

export function clampAssistantPaneWidth (width: number): number {
  if (typeof window === 'undefined') {
    return ASSISTANT_PANE_DEFAULT_WIDTH
  }

  const maxByViewport = Math.floor(window.innerWidth * 0.5)
  const max = Math.min(ASSISTANT_PANE_MAX_WIDTH, maxByViewport)

  return Math.min(max, Math.max(ASSISTANT_PANE_MIN_WIDTH, Math.round(width)))
}

export function paneWidthFromClientX (clientX: number): number {
  return clampAssistantPaneWidth(window.innerWidth - clientX)
}

export function useAssistantPaneResize () {
  const store = useBooksyAssistantStore()
  const { paneWidth } = storeToRefs(store)
  let activePointerId: number | null = null

  const setPaneWidth = (width: number): void => {
    store.paneWidth = clampAssistantPaneWidth(width)
  }

  const stopResize = (): void => {
    activePointerId = null
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    window.removeEventListener('pointercancel', onPointerUp)
  }

  const onPointerMove = (event: PointerEvent): void => {
    if (activePointerId !== event.pointerId) {
      return
    }

    setPaneWidth(paneWidthFromClientX(event.clientX))
  }

  const onPointerUp = (event: PointerEvent): void => {
    if (activePointerId !== event.pointerId) {
      return
    }

    stopResize()
  }

  const startResize = (event: PointerEvent): void => {
    if (event.button !== 0) {
      return
    }

    event.preventDefault()
    activePointerId = event.pointerId
    setPaneWidth(paneWidthFromClientX(event.clientX))
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
  }

  onBeforeUnmount(() => {
    stopResize()
  })

  return {
    paneWidth,
    setPaneWidth,
    startResize,
  }
}
